import { Component, computed, inject, signal } from '@angular/core';

import { injectTranslate, TranslatePipe } from '@core/i18n-legacy';
import { TellentMember } from '@core/tellent-types';
import { TellentManageRedirectService } from '@core/tellent-user-invitation-logic';
import { UserInvitationData } from '@core/tellent-user-invitation-types';
import {
  ButtonComponent,
  DialogBodyComponent,
  DialogComponent,
  IconComponent,
} from '@core/user-interface-legacy';

interface MemberAddedSuccessDialogCopy {
  heading: string;
  subheading: string;
}

type UserInvitationFlowT =
  | 'invite_via_email'
  | 'generate_username_and_password'
  | 'add_employees_without_access';

@Component({
  selector: 'tla-member-added-success-dialog',
  templateUrl: './member-added-success-dialog.component.html',
  styleUrls: ['./member-added-success-dialog.component.less'],
  imports: [DialogBodyComponent, ButtonComponent, TranslatePipe, IconComponent],
  providers: [TellentManageRedirectService],
})
export class MemberAddedSuccessDialogComponent extends DialogComponent<UserInvitationData> {
  private readonly t = injectTranslate();
  private readonly redirectService = inject(TellentManageRedirectService);

  public invitationFlow = signal<UserInvitationFlowT>('invite_via_email');
  public payload = signal<TellentMember[]>([]);

  public copyRecord = computed<Record<UserInvitationFlowT, MemberAddedSuccessDialogCopy>>(() => {
    const count = this.payload().length;
    const member: TellentMember = this.payload()[0];
    const fullName = `${member.firstName} ${member.lastName}`;

    return {
      invite_via_email: {
        heading: this.t('tlc.members.member_added_success_dialog.heading.invite_via_email', {
          count,
        }),
        subheading: this.t('tlc.members.member_added_success_dialog.subheading.invite_via_email', {
          count,
          email: member.email,
        }),
      },
      generate_username_and_password: {
        heading: this.t(
          'tlc.members.member_added_success_dialog.heading.generate_username_and_password',
          { count },
        ),
        subheading: this.t(
          'tlc.members.member_added_success_dialog.subheading.generate_username_and_password',
          { count, fullName },
        ),
      },
      add_employees_without_access: {
        heading: this.t(
          'tlc.members.member_added_success_dialog.heading.add_employees_without_access',
          { count },
        ),
        subheading: this.t(
          'tlc.members.member_added_success_dialog.subheading.add_employees_without_access',
          { count },
        ),
      },
    };
  });

  public ngOnInit(): void {
    this.invitationFlow.set(this.resolveInvitationPath(this.dialogData));
    this.payload.set(this.dialogData.payload!);
  }

  public startPreonboarding(): void {
    const memberGuids = this.payload().map(member => member.guid);
    this.redirectService.sendMembersForPreonboarding(memberGuids);
  }

  public finishAndClose(): void {
    this.dialogRef.close({ success: true });
  }

  public cancel(): void {
    this.dialogRef.close();
  }

  private resolveInvitationPath(invitationData: UserInvitationData): UserInvitationFlowT {
    const { inviteMethod } = invitationData;

    if (inviteMethod === 'login') {
      return 'generate_username_and_password';
    } else if (inviteMethod === 'email') {
      return 'invite_via_email';
    } else {
      return 'add_employees_without_access';
    }
  }
}
